// Builds the DOM once and reflects the store on every change. Interaction is delegated to
// the supplied handlers, so the visual layer (this file) stays independent of the logic layer.
import { ChatStore, ChatStoreState, captionFor, controlsActive } from './store';
import type { ChatState, LanguageOption } from './types';
import { setWaveformPaused, startWaveform, stopWaveform } from './waveform';
import { clearLiveTranscript, updateLiveTranscript } from './live-transcript';
import { INPUT_PLACEHOLDERS, MIN_SEND_CHARS, primarySubtag } from './config';

const ASSET = {
  micActive: '/images/buttons/microphone_active.png',
  micInactive: '/images/buttons/microphone_inactive.png',
  sendActive: '/images/buttons/send_message_active.png',
  sendInactive: '/images/buttons/send_message_inactive.png',
  pause: '/images/buttons/pause_active.png',
  trash: '/images/buttons/trash_active.png',
  replay: '/images/buttons/replay.png',
  langActive: '/images/buttons/language_activestate.png',
  logo: '/images/logo/FTI_logo.png',
};

/** Hardcoded "type your question" for the selected language; falls back to English. */
function placeholderFor(code: string): string {
  return INPUT_PLACEHOLDERS[primarySubtag(code)] ?? INPUT_PLACEHOLDERS.en;
}

/**
 * Shrink the answer caption as it gets longer so more of it fits without scrolling.
 * Values stay responsive (clamp with a vw term) so they also adapt to screen width; the
 * `.caption` box has a max-height + overflow-y:auto, so the extreme case scrolls rather
 * than being clipped — hence we keep a sensible floor (~13px) instead of going tiny.
 */
function responseFontSize(length: number): string {
  if (length <= 90) return ''; // default: CSS clamp(15px, 4.6vw, 20px)
  if (length <= 180) return 'clamp(15px, 4.4vw, 18px)';
  if (length <= 300) return 'clamp(14px, 4vw, 16px)';
  if (length <= 450) return 'clamp(13px, 3.6vw, 15px)';
  return 'clamp(13px, 3.4vw, 14px)';
}

const ALL_STATES: ChatState[] = [
  'loading', 'policy', 'wait', 'keyboard', 'listening', 'processing', 'response', 'eager',
];

export interface ChatHandlers {
  onPolicyConfirm(): void;
  onPolicyCancel(): void;
  onMicTap(): void;
  onSendTap(): void;
  onTrashTap(): void;
  onLangSelect(lang: LanguageOption): void;
  onReplayTap(): void;
  onInputFocus(): void;
  onInputBlur(): void;
  onInputChange(value: string): void;
  /** Recording time left, in ms, for the countdown in the pill (a query, not an event). */
  recordingRemainingMs(): number;
}

export interface ViewApi {
  /** The visible video element. */
  readonly video: HTMLVideoElement;
  /** The hidden twin, used to buffer the next status video before a swap. */
  readonly backVideo: HTMLVideoElement;
  /** Make the back video the visible one (and vice versa). */
  swapVideos(): void;
  screen: HTMLDivElement;
  /** Container for the inline LMLM logo SVG on the loading screen (animated by loading.ts). */
  loadingLogo: HTMLDivElement;
  /** Set the loading pill progress, 0..1. */
  setLoadingProgress(fraction: number): void;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (cls) node.className = cls;
  return node;
}

/**
 * Keep --app-height in sync with the visual viewport. iOS Safari never overlays its
 * keyboard: it shrinks the visual viewport and pans the page to reveal the focused input,
 * pushing the site up. Sizing #app from the visual viewport makes the layout compress in
 * place (the keyboard-open rules hide the lower chrome), and resetting the pan keeps the
 * top of the app on screen. No-ops gracefully where visualViewport is unavailable.
 */
function trackVisualViewport(): void {
  const vv = window.visualViewport;
  if (!vv) return;
  const sync = () => {
    document.documentElement.style.setProperty('--app-height', `${Math.round(vv.height)}px`);
    // Cancel the automatic pan-to-input: with the app compressed to the visible area the
    // input is already on screen, so any residual scroll just hides the top of the site.
    if (window.scrollY || window.scrollX) window.scrollTo(0, 0);
  };
  vv.addEventListener('resize', sync);
  vv.addEventListener('scroll', sync);
  sync();
}

export function mountView(
  root: HTMLElement,
  store: ChatStore,
  handlers: ChatHandlers,
): ViewApi {
  trackVisualViewport();
  root.innerHTML = '';
  const screen = el('div', 'screen');

  // --- Video zone ---
  const videoZone = el('div', 'video-zone');
  const makeVideo = (): HTMLVideoElement => {
    const v = el('video', 'video');
    v.muted = true;
    v.loop = true;
    v.setAttribute('playsinline', '');
    return v;
  };
  let video = makeVideo();
  let backVideo = makeVideo();
  backVideo.classList.add('video-back');
  const placeholder = el('div', 'video-placeholder');
  placeholder.innerHTML = '<svg viewBox="0 0 100 100" aria-hidden="true"><polygon points="40,28 40,72 74,50"/></svg>';
  const replayBtn = el('button', 'replay-btn');
  replayBtn.setAttribute('aria-label', 'Replay answer');
  const replayImg = el('img');
  replayImg.src = ASSET.replay;
  replayImg.alt = '';
  replayBtn.appendChild(replayImg);
  replayBtn.addEventListener('click', () => handlers.onReplayTap());

  // --- Hamburger menu (overlays top-left of the video) ---
  const menu = el('div', 'menu');
  const menuToggle = el('button', 'menu-toggle');
  menuToggle.type = 'button';
  menuToggle.setAttribute('aria-label', 'Open menu');
  menuToggle.setAttribute('aria-haspopup', 'true');
  menuToggle.setAttribute('aria-expanded', 'false');
  menuToggle.innerHTML = '<span></span><span></span><span></span>';
  const menuPanel = el('div', 'menu-panel');
  menuPanel.setAttribute('role', 'menu');
  const privacyLink = el('a', 'menu-item');
  privacyLink.href = 'https://lean-mean-learning-machine.com/privacy-policy/';
  privacyLink.target = '_blank';
  privacyLink.rel = 'noopener noreferrer';
  privacyLink.setAttribute('role', 'menuitem');
  privacyLink.textContent = 'Privacy Policy';
  const siteLink = el('a', 'menu-item');
  siteLink.href = 'https://lean-mean-learning-machine.com/';
  siteLink.target = '_blank';
  siteLink.rel = 'noopener noreferrer';
  siteLink.setAttribute('role', 'menuitem');
  siteLink.textContent = 'Lean Mean Learning Machine';
  const technopolisLink = el('a', 'menu-item');
  technopolisLink.href = 'https://technopolis.be/';
  technopolisLink.target = '_blank';
  technopolisLink.rel = 'noopener noreferrer';
  technopolisLink.setAttribute('role', 'menuitem');
  technopolisLink.textContent = 'Technopolis';
  const ftiLink = el('a', 'menu-item');
  ftiLink.href = 'https://www.ftifestival.be/';
  ftiLink.target = '_blank';
  ftiLink.rel = 'noopener noreferrer';
  ftiLink.setAttribute('role', 'menuitem');
  ftiLink.textContent = 'FTI';
  // Non-interactive informational footer (not a menuitem).
  const poweredBy = el('div', 'menu-powered');
  poweredBy.textContent = 'Powered by Lean Mean Learning Machine, Technopolis & FTI';

  // --- Social icon row (bottom of the menu) ---
  // Yellow PNGs served from assets/images/social media icons/ (the folder name has spaces, so
  // the URL is %20-encoded). One <a class="menu-social-link"><img></a> per network.
  const social = el('div', 'menu-social');
  const SOCIAL_LINKS: { key: string; label: string; file: string; url: string }[] = [
    {
      key: 'instagram',
      label: 'Instagram',
      file: 'SocMed-Yellow-Insta.png',
      url: 'https://www.instagram.com/leanmeanlearningmachine',
    },
    {
      key: 'linkedin',
      label: 'LinkedIn',
      file: 'SocMed-Yellow-LinkedIn.png',
      url: 'https://www.linkedin.com/company/98961355',
    },
    {
      key: 'website',
      label: 'Website',
      file: 'SocMed-Yellow-Website.png',
      url: 'https://lean-mean-learning-machine.com/',
    },
  ];
  for (const s of SOCIAL_LINKS) {
    const link = el('a', 'menu-social-link');
    link.href = s.url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.dataset.social = s.key;
    link.setAttribute('aria-label', s.label);
    const img = el('img');
    img.src = `/images/social%20media%20icons/${s.file}`;
    img.alt = '';
    link.appendChild(img);
    social.appendChild(link);
  }

  // Order: LMLM site, Technopolis, FTI, Privacy Policy, the powered-by footer, then the social row.
  menuPanel.append(siteLink, technopolisLink, ftiLink, privacyLink, poweredBy, social);
  menu.append(menuToggle, menuPanel);

  function setMenuOpen(open: boolean): void {
    menu.classList.toggle('open', open);
    menuToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    menuToggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
  }
  menuToggle.addEventListener('click', (e) => {
    e.stopPropagation();
    setMenuOpen(!menu.classList.contains('open'));
  });
  // Close when a link is chosen, or on any outside click / Escape.
  menuPanel.addEventListener('click', () => setMenuOpen(false));
  document.addEventListener('click', (e) => {
    if (menu.classList.contains('open') && !menu.contains(e.target as Node)) setMenuOpen(false);
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') setMenuOpen(false);
  });

  videoZone.append(video, backVideo, placeholder, replayBtn);

  // --- Controls ---
  const controls = el('div', 'controls');
  const caption = el('div', 'caption');

  const pill = el('div', 'pill');
  const input = el('input', 'text-input');
  input.type = 'text';
  input.autocomplete = 'off';
  input.setAttribute('enterkeyhint', 'send');
  input.addEventListener('focus', () => handlers.onInputFocus());
  input.addEventListener('blur', () => handlers.onInputBlur());
  input.addEventListener('input', () => handlers.onInputChange(input.value));

  const recTimer = el('span', 'rec-timer');
  const waveform = el('canvas', 'waveform');
  const dots = el('div', 'dots');
  dots.innerHTML = '<span></span><span></span><span></span>';

  const micBtn = el('button', 'icon-btn mic-btn');
  micBtn.setAttribute('aria-label', 'Tap to talk');
  const micImg = el('img');
  micImg.alt = '';
  micBtn.appendChild(micImg);
  micBtn.addEventListener('click', () => handlers.onMicTap());

  const sendBtn = el('button', 'icon-btn send-btn');
  sendBtn.setAttribute('aria-label', 'Send');
  const sendImg = el('img');
  sendImg.alt = '';
  sendBtn.appendChild(sendImg);
  sendBtn.addEventListener('click', () => handlers.onSendTap());

  pill.append(recTimer, input, waveform, dots, micBtn, sendBtn);

  // --- Recording controls (below the pill, `listening` only) ---
  // While recording the pill holds just the countdown + the recording line; the buttons
  // live in this row instead: discard (left), pause / record-to-append (middle), send (right).
  const recActions = el('div', 'rec-actions');
  function recButton(cls: string, label: string, src: string, onTap: () => void) {
    const button = el('button', `rec-btn ${cls}`);
    button.type = 'button';
    button.setAttribute('aria-label', label);
    const img = el('img');
    img.src = src;
    img.alt = '';
    button.appendChild(img);
    button.addEventListener('click', onTap);
    recActions.appendChild(button);
    return { button, img };
  }
  const trash = recButton('rec-trash', 'Delete recording', ASSET.trash, () => handlers.onTrashTap());
  const recToggle = recButton('rec-toggle', 'Pause recording', ASSET.pause, () => handlers.onMicTap());
  const recSend = recButton('rec-send', 'Send', ASSET.sendActive, () => handlers.onSendTap());

  // Recording countdown at the left of the pill. Purely visual: it shows the recorder's
  // own time budget, which stands still while the recording is paused.
  let countdownTimer = 0;
  let countdownFrozen = false; // once sent, hold the last value until the state changes
  function formatCountdown(msLeft: number): string {
    const total = Math.max(0, Math.ceil(msLeft / 1000));
    const mm = String(Math.floor(total / 60)).padStart(2, '0');
    const ss = String(total % 60).padStart(2, '0');
    return `${mm}:${ss}`;
  }
  function startCountdown(): void {
    if (countdownTimer) return;
    const tick = () => {
      if (!countdownFrozen) recTimer.textContent = formatCountdown(handlers.recordingRemainingMs());
    };
    tick();
    countdownTimer = window.setInterval(tick, 200);
  }
  function stopCountdown(): void {
    if (!countdownTimer) return;
    clearInterval(countdownTimer);
    countdownTimer = 0;
  }

  const languages = el('div', 'languages');
  // Language buttons are rebuilt whenever the config's language set changes — the real
  // config arrives asynchronously after mount, replacing the single loading placeholder.
  let langButtons: { lang: LanguageOption; button: HTMLButtonElement }[] = [];
  let langSignature = '';

  function buildLanguages(langs: LanguageOption[]): void {
    languages.innerHTML = '';
    langButtons = langs.map((lang) => {
      // NL/FR/EN have button artwork; any other configured language is a text button.
      const button = el('button', lang.image ? 'lang' : 'lang lang-text');
      button.dataset.code = lang.code;
      button.setAttribute('aria-label', lang.name);
      button.title = lang.name;
      if (lang.image) {
        const img = el('img');
        img.src = lang.image;
        img.alt = lang.label;
        const overlay = el('img', 'lang-active');
        overlay.src = ASSET.langActive;
        overlay.alt = '';
        button.append(img, overlay);
      } else {
        button.textContent = lang.label;
      }
      button.addEventListener('click', () => handlers.onLangSelect(lang));
      languages.appendChild(button);
      return { lang, button };
    });
  }

  const logo = el('img', 'logo');
  logo.src = ASSET.logo;
  logo.alt = 'Flanders Technology & Innovation';

  controls.append(caption, pill, recActions, languages, logo);

  // --- Policy overlay ---
  const overlay = el('div', 'policy-overlay');
  const policyText = el('div', 'policy-text');
  const policyActions = el('div', 'policy-actions');
  const confirmBtn = el('button', 'policy-btn confirm');
  confirmBtn.textContent = 'I agree';
  confirmBtn.addEventListener('click', () => handlers.onPolicyConfirm());
  const cancelBtn = el('button', 'policy-btn cancel');
  cancelBtn.textContent = 'Cancel';
  cancelBtn.addEventListener('click', () => handlers.onPolicyCancel());
  policyActions.append(confirmBtn, cancelBtn);
  overlay.append(policyText, policyActions);

  // --- Error overlay (blocking) ---
  const errorOverlay = el('div', 'error-overlay');
  const errorText = el('div', 'error-text');
  errorOverlay.appendChild(errorText);

  // --- Loading overlay (branded boot screen: animated logo + progress pill) ---
  const loadingOverlay = el('div', 'loading-overlay');
  const loadingLogo = el('div', 'loading-logo');
  loadingLogo.setAttribute('aria-hidden', 'true');
  const loadingBar = el('div', 'loading-bar');
  loadingBar.setAttribute('role', 'progressbar');
  loadingBar.setAttribute('aria-label', 'Loading');
  const loadingFill = el('div', 'loading-bar-fill');
  loadingBar.appendChild(loadingFill);
  loadingOverlay.append(loadingLogo, loadingBar);
  function setLoadingProgress(fraction: number): void {
    const pct = Math.max(0, Math.min(1, fraction)) * 100;
    loadingFill.style.width = `${pct}%`;
    loadingBar.setAttribute('aria-valuenow', String(Math.round(pct)));
  }
  setLoadingProgress(0);

  // The menu is a direct child of .screen (not .video-zone) so its open dropdown is not
  // clipped by the video zone's overflow:hidden and can stack above the controls/keyboard.
  screen.append(videoZone, controls, menu, overlay, errorOverlay, loadingOverlay);
  root.appendChild(screen);

  // --- Dev state switcher (development builds only; tree-shaken out of production) ---
  let devButtons: HTMLButtonElement[] = [];
  if (import.meta.env.DEV) {
    const devbar = el('div', 'devbar');
    devButtons = ALL_STATES.map((s) => {
      const b = el('button');
      b.textContent = s;
      b.dataset.state = s;
      b.addEventListener('click', () => store.set({ state: s }));
      devbar.appendChild(b);
      return b;
    });
    root.appendChild(devbar);
    window.addEventListener('keydown', (e) => {
      if (e.key.toLowerCase() === 'd') devbar.classList.toggle('hidden');
    });
  }

  // --- Render ---
  function render(s: ChatStoreState): void {
    screen.dataset.state = s.state;
    screen.classList.toggle('keyboard-open', s.state === 'keyboard');
    errorOverlay.classList.toggle('visible', !!s.error);
    if (s.error) errorText.textContent = s.error;
    const active = !s.locked && controlsActive(s.state);
    // Send stays disabled until the typed question has at least MIN_SEND_CHARS characters.
    const canSend = active && s.inputValue.trim().length >= MIN_SEND_CHARS;

    // While listening (and holding the finished sentence), the live word-by-word
    // transcript owns the caption; updates are diff-based so re-renders never
    // clobber the fade-in animation. Everywhere else, plain caption text.
    const captionText = captionFor(s);
    const liveWords = s.state === 'listening' && !s.overrideCaption && s.liveTranscript.trim();
    if (liveWords) {
      updateLiveTranscript(caption, s.liveTranscript);
    } else {
      clearLiveTranscript();
      caption.textContent = captionText;
    }
    caption.style.fontSize = s.state === 'response' ? responseFontSize(captionText.length) : '';
    if (s.state === 'policy') policyText.textContent = s.config.policy;

    if (input.value !== s.inputValue) input.value = s.inputValue;
    input.placeholder = placeholderFor(s.selectedLang.code);
    input.disabled = !active;
    // (While recording these two are hidden; the row below the pill takes over.)
    micImg.src = active ? ASSET.micActive : ASSET.micInactive;
    sendImg.src = canSend ? ASSET.sendActive : ASSET.sendInactive;
    micBtn.disabled = !active;
    sendBtn.disabled = !canSend;

    if (s.state === 'listening') {
      const phase = s.recPhase;
      const live = phase === 'recording';
      countdownFrozen = phase === 'sending';
      if (phase === 'out-of-time') recTimer.textContent = formatCountdown(0);
      startWaveform(waveform);
      setWaveformPaused(!live);
      startCountdown();
      // Middle button: pause while recording, the record icon again once paused. Out of
      // time it shows the greyed mic but stays tappable, so the tap can explain why.
      recToggle.img.src = live
        ? ASSET.pause
        : phase === 'paused' ? ASSET.micActive : ASSET.micInactive;
      recToggle.button.classList.toggle('is-mic', !live);
      recToggle.button.setAttribute('aria-label', live ? 'Pause recording' : 'Continue recording');
      const sending = phase === 'sending' || s.locked;
      trash.button.disabled = sending;
      recToggle.button.disabled = sending;
      recSend.button.disabled = sending;
      pill.classList.toggle('rec-paused', !live);
      pill.classList.toggle('rec-spent', phase === 'out-of-time');
    } else {
      pill.classList.remove('rec-paused', 'rec-spent');
      stopWaveform();
      stopCountdown();
    }

    // Rebuild the language buttons when the config's language set changes (the real config
    // replaces the loading placeholder after mount).
    const sig = s.config.languages.map((l) => l.code).join(',');
    if (sig !== langSignature) {
      langSignature = sig;
      buildLanguages(s.config.languages);
    }
    for (const { lang, button } of langButtons) {
      button.classList.toggle('selected', lang.code === s.selectedLang.code);
    }
    for (const b of devButtons) b.classList.toggle('active', b.dataset.state === s.state);
  }

  store.subscribe(render);
  return {
    get video() {
      return video;
    },
    get backVideo() {
      return backVideo;
    },
    swapVideos() {
      [video, backVideo] = [backVideo, video];
      video.classList.remove('video-back');
      backVideo.classList.add('video-back');
    },
    screen,
    loadingLogo,
    setLoadingProgress,
  };
}
